import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Bell, CheckCircle, Clock, AlertTriangle, MessageSquare,
  Send, User, Calendar, ArrowUpRight, ChevronDown, Loader2, X,
  ShieldAlert, Ban, UserPlus, RotateCcw
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';
import QuickAssignModal from '@/components/QuickAssignModal';
import DateRangeSelect from '@/components/DateRangeSelect';
import SiteDeptFilter from '@/components/SiteDeptFilter';

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, label: 'Pending' },
  in_progress: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: ArrowUpRight, label: 'In Progress' },
  completed: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, label: 'Completed' },
  skipped: { color: 'bg-gray-100 text-gray-600 border-gray-300', icon: X, label: 'Skipped' },
  escalated: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: ShieldAlert, label: 'Escalated' },
  blocked: { color: 'bg-red-100 text-red-800 border-red-300', icon: Ban, label: 'Blocked' },
};

const priorityConfig = {
  urgent: { color: 'bg-red-500 text-white', label: 'Urgent' },
  high: { color: 'bg-orange-100 text-orange-800', label: 'High' },
  medium: { color: 'bg-yellow-100 text-yellow-800', label: 'Medium' },
  low: { color: 'bg-gray-100 text-gray-600', label: 'Low' },
};

const DEMO_ACTIONS = [
  { action_id: 'act_001', action_type: 'internal', source_module: 'Smart Alternates', assign_to_name: 'Sarah Chen', assign_to_email: 'sarah.chen@company.com', assigned_by_name: 'Demo User', assigned_by_email: 'test@example.com', assigned_by: 'demo', department: 'Engineering', details: 'Validate STM32F405VGT6 as FFF alternate for STM32F407VGT6 — check CCM RAM usage', priority: 'high', status: 'pending', due_date: new Date(Date.now() - 2*86400000).toISOString(), context: { mpn: 'STM32F407VGT6', bomName: 'Main PCB v2.1', sourceModule: 'Smart Alternates', savings: '$4,600' }, created_at: new Date(Date.now() - 5*86400000).toISOString(), is_overdue: true, days_overdue: 2, history: [{ status: 'pending', changed_by_name: 'Demo User', timestamp: new Date(Date.now() - 5*86400000).toISOString(), note: 'Action created' }], responses: [] },
  { action_id: 'act_002', action_type: 'internal', source_module: 'Part Renegotiations', assign_to_name: 'Mike Johnson', assign_to_email: 'mike.johnson@company.com', assigned_by_name: 'Demo User', assigned_by_email: 'test@example.com', assigned_by: 'demo', department: 'Procurement', details: 'Renegotiate GRM188R71H104KA93D pricing with DigiKey — market avg is $0.018 vs your $0.025', priority: 'urgent', status: 'in_progress', due_date: new Date(Date.now() + 2*86400000).toISOString(), context: { mpn: 'GRM188R71H104KA93D', sourceModule: 'Part Renegotiations', savings: '$2,450' }, created_at: new Date(Date.now() - 3*86400000).toISOString(), is_overdue: false, days_overdue: 0, history: [{ status: 'pending', changed_by_name: 'Demo User', timestamp: new Date(Date.now() - 3*86400000).toISOString(), note: 'Action created' }, { status: 'in_progress', changed_by_name: 'Mike Johnson', timestamp: new Date(Date.now() - 1*86400000).toISOString(), note: 'Started negotiation with DigiKey rep' }], responses: [{ user: 'Mike Johnson', text: 'Called DigiKey — they offered $0.021 for 350K qty. Pushing for $0.019.', date: new Date(Date.now() - 1*86400000).toISOString() }] },
  { action_id: 'act_003', action_type: 'internal', source_module: 'BOM Intelligence', assign_to_name: 'Demo User', assign_to_email: 'test@example.com', assigned_by_name: 'Anna Lee', assigned_by_email: 'anna.lee@company.com', assigned_by: 'anna', department: 'R&D', details: 'Review Q1 BOM cost optimization report and approve alternates for IoT Sensor Board', priority: 'medium', status: 'pending', due_date: new Date(Date.now() + 5*86400000).toISOString(), context: { bomName: 'IoT Sensor Board v1.0', sourceModule: 'BOM Intelligence' }, created_at: new Date(Date.now() - 1*86400000).toISOString(), is_overdue: false, days_overdue: 0, history: [{ status: 'pending', changed_by_name: 'Anna Lee', timestamp: new Date(Date.now() - 1*86400000).toISOString(), note: 'Action created' }], responses: [] },
  { action_id: 'act_004', action_type: 'internal', source_module: 'Risk Radar', assign_to_name: 'Emily Davis', assign_to_email: 'emily.davis@company.com', assigned_by_name: 'Demo User', assigned_by_email: 'test@example.com', assigned_by: 'demo', department: 'Quality', details: 'Run qualification tests on IS42S16160J-6TLI as alternate for MT48LC16M16A2 — EOL approaching', priority: 'high', status: 'escalated', due_date: new Date(Date.now() - 4*86400000).toISOString(), context: { mpn: 'MT48LC16M16A2', sourceModule: 'Risk Radar' }, created_at: new Date(Date.now() - 7*86400000).toISOString(), is_overdue: true, days_overdue: 4, history: [{ status: 'pending', changed_by_name: 'Demo User', timestamp: new Date(Date.now() - 7*86400000).toISOString(), note: 'Action created' }, { status: 'escalated', changed_by_name: 'Emily Davis', timestamp: new Date(Date.now() - 2*86400000).toISOString(), note: 'Need budget approval for test equipment' }], responses: [{ user: 'Emily Davis', text: 'Lab equipment not available until next week. Escalating for faster procurement.', date: new Date(Date.now() - 2*86400000).toISOString() }] },
  { action_id: 'act_005', action_type: 'internal', source_module: 'Buy Now / Hold', assign_to_name: 'John Smith', assign_to_email: 'john.smith@company.com', assigned_by_name: 'Demo User', assigned_by_email: 'test@example.com', assigned_by: 'demo', department: 'Production', details: 'Place blanket order for NRF52840 — AI recommends Buy Now due to 20-week lead time increase', priority: 'urgent', status: 'completed', due_date: new Date(Date.now() - 1*86400000).toISOString(), context: { mpn: 'NRF52840-QIAA-R', sourceModule: 'Buy Now / Hold', savings: '$8,200' }, created_at: new Date(Date.now() - 10*86400000).toISOString(), is_overdue: false, days_overdue: 0, resolution: 'Order placed for 8000 units at $5.10/unit. Delivery confirmed for Q2.', history: [{ status: 'pending', changed_by_name: 'Demo User', timestamp: new Date(Date.now() - 10*86400000).toISOString(), note: 'Created' }, { status: 'in_progress', changed_by_name: 'John Smith', timestamp: new Date(Date.now() - 8*86400000).toISOString(), note: 'Getting quotes' }, { status: 'completed', changed_by_name: 'John Smith', timestamp: new Date(Date.now() - 1*86400000).toISOString(), note: 'PO placed' }], responses: [{ user: 'John Smith', text: 'PO #2026-Q1-4521 placed with Mouser. 8000 units confirmed at $5.10.', date: new Date(Date.now() - 1*86400000).toISOString() }] },
];

const ActionHistory = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [filterSite, setFilterSite] = useState('All Sites');
  const [filterDeptAction, setFilterDeptAction] = useState('All Departments');
  const [selectedAction, setSelectedAction] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [showReassign, setShowReassign] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const userRes = await api.get('/auth/me');
        setCurrentUserEmail(userRes.data.email || 'test@example.com');
      } catch {
        setCurrentUserEmail('test@example.com');
      }
      fetchActions();
    };
    init();
  }, []);

  const fetchActions = async () => {
    try {
      const res = await api.get('/actions/list');
      const fetched = res.data.all_actions || res.data.actions || [];
      setIsSuperUser(res.data.is_super_user || false);
      setActions(fetched.length > 0 ? fetched : DEMO_ACTIONS);
    } catch {
      setActions(DEMO_ACTIONS);
    } finally { setLoading(false); }
  };

  const handleReply = async (actionId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/actions/${actionId}/respond`, { text: replyText });
    } catch {}
    setActions(prev => prev.map(a => a.action_id === actionId ? { ...a, responses: [...(a.responses || []), { user: 'You', text: replyText, date: new Date().toISOString() }] } : a));
    if (selectedAction?.action_id === actionId) {
      setSelectedAction(prev => ({ ...prev, responses: [...(prev.responses || []), { user: 'You', text: replyText, date: new Date().toISOString() }] }));
    }
    toast.success('Response added');
    setReplyText('');
  };

  const handleStatusChange = async (actionId, newStatus) => {
    try {
      await api.put(`/actions/${actionId}/status`, { status: newStatus, note: statusNote || `Marked as ${newStatus}` });
    } catch {}
    setActions(prev => prev.map(a => a.action_id === actionId ? { ...a, status: newStatus } : a));
    if (selectedAction?.action_id === actionId) setSelectedAction(prev => ({ ...prev, status: newStatus }));
    toast.success(`Status changed to ${newStatus}`);
    setStatusNote('');
  };

  const filtered = actions.filter(a => {
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterType !== 'all' && a.action_type !== filterType) return false;
    return true;
  });

  const myActions = filtered.filter(a => a.assign_to_email === currentUserEmail);
  const assignedByMe = filtered.filter(a => a.assigned_by === 'demo' || a.assigned_by_email === currentUserEmail);
  const completedActions = filtered.filter(a => ['completed', 'skipped'].includes(a.status));
  const activeActions = filtered.filter(a => !['completed', 'skipped'].includes(a.status));
  const overdueActions = actions.filter(a => a.is_overdue);

  const kpis = [
    { label: 'Pending', count: actions.filter(a => a.status === 'pending').length, cls: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
    { label: 'In Progress', count: actions.filter(a => a.status === 'in_progress').length, cls: 'text-blue-600 bg-blue-50 border-blue-200' },
    { label: 'Escalated', count: actions.filter(a => a.status === 'escalated').length, cls: 'text-orange-600 bg-orange-50 border-orange-200' },
    { label: 'Blocked', count: actions.filter(a => a.status === 'blocked').length, cls: 'text-red-600 bg-red-50 border-red-200' },
    { label: 'Overdue', count: overdueActions.length, cls: 'text-red-700 bg-red-50 border-red-300' },
    { label: 'Completed', count: actions.filter(a => a.status === 'completed').length, cls: 'text-green-600 bg-green-50 border-green-200' },
  ];

  const renderActionCard = (action) => {
    const sc = statusConfig[action.status] || statusConfig.pending;
    const pc = priorityConfig[action.priority] || priorityConfig.medium;
    const StatusIcon = sc.icon;
    const isOverdue = action.is_overdue;
    const dueDate = action.due_date ? new Date(action.due_date) : null;
    const isPastDue = dueDate && dueDate < new Date() && !['completed', 'skipped'].includes(action.status);

    return (
      <Card
        key={action.action_id}
        className={`bg-white shadow-sm hover:shadow-sm transition-all cursor-pointer border-l-4 ${isPastDue ? 'border-l-red-500 bg-red-50/30' : action.status === 'escalated' ? 'border-l-orange-500' : action.status === 'in_progress' ? 'border-l-blue-500' : action.status === 'completed' ? 'border-l-green-500' : 'border-l-gray-300'} border border-gray-200`}
        onClick={() => setSelectedAction(action)}
        data-testid={`action-card-${action.action_id}`}
      >
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start gap-3">
            {/* Assignee Avatar */}
            <Avatar className="h-10 w-10 flex-shrink-0">
              <AvatarFallback className={`${action.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'} text-xs font-bold`}>
                {action.assign_to_name?.split(' ').map(n => n[0]).join('') || '?'}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              {/* Top Row: Status, Priority, Source, MPN */}
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                <Badge className={`${sc.color} text-[10px] flex items-center gap-0.5`}>
                  <StatusIcon className="h-2.5 w-2.5" /> {sc.label}
                </Badge>
                <Badge className={`${pc.color} text-[10px]`}>{pc.label}</Badge>
                {action.source_module && <Badge variant="outline" className="text-[10px]">{action.source_module}</Badge>}
                {action.context?.mpn && <Badge className="bg-blue-50 text-blue-800 text-[10px] font-mono border border-blue-200">{action.context.mpn}</Badge>}
                {isOverdue && <Badge className="bg-red-500 text-white text-[10px] animate-pulse">OVERDUE ({action.days_overdue}d)</Badge>}
              </div>

              {/* Details */}
              <p className="text-sm text-gray-900 font-medium leading-snug">{action.details}</p>

              {/* Meta Row */}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><User className="h-3 w-3" /> {action.assign_to_name}</span>
                <span className="flex items-center gap-1"><Send className="h-3 w-3" /> by {action.assigned_by_name}</span>
                {dueDate && (
                  <span className={`flex items-center gap-1 ${isPastDue ? 'text-red-600 font-semibold' : ''}`}>
                    <Calendar className="h-3 w-3" /> Due {dueDate.toLocaleDateString()}
                  </span>
                )}
                {action.responses?.length > 0 && (
                  <span className="flex items-center gap-1 text-blue-600"><MessageSquare className="h-3 w-3" />{action.responses.length}</span>
                )}
                {action.context?.savings && <span className="text-green-600 font-medium">Savings: {action.context.savings}</span>}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
              {action.status === 'pending' && (
                <>
                  <Button size="sm" className="bg-blue-600 text-white h-6 text-[10px] px-2" onClick={() => handleStatusChange(action.action_id, 'in_progress')}>
                    Start
                  </Button>
                  <Button size="sm" className="bg-green-600 text-white h-6 text-[10px] px-2" onClick={() => handleStatusChange(action.action_id, 'completed')}>
                    Done
                  </Button>
                </>
              )}
              {action.status === 'in_progress' && (
                <>
                  <Button size="sm" className="bg-green-600 text-white h-6 text-[10px] px-2" onClick={() => handleStatusChange(action.action_id, 'completed')}>
                    Complete
                  </Button>
                  <Button size="sm" variant="outline" className="text-orange-600 border-orange-300 h-6 text-[10px] px-2" onClick={() => handleStatusChange(action.action_id, 'escalated')}>
                    Escalate
                  </Button>
                </>
              )}
              {action.status === 'escalated' && (
                <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 h-6 text-[10px] px-2" onClick={() => handleStatusChange(action.action_id, 'in_progress')}>
                  Resume
                </Button>
              )}
              {action.status === 'blocked' && (
                <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 h-6 text-[10px] px-2" onClick={() => handleStatusChange(action.action_id, 'in_progress')}>
                  Unblock
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderActionList = (list) => (
    list.length === 0 ? (
      <Card className="bg-white border-gray-200"><CardContent className="py-12 text-center text-gray-500">
        <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />No actions found.
      </CardContent></Card>
    ) : (
      <div className="space-y-6">{list.map(renderActionCard)}</div>
    )
  );

  return (
    <Layout title="Action Center" subtitle="Track, communicate, and resolve all actionables">
      <div className="space-y-6" data-testid="action-history-page">
        {/* KPI Row */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {kpis.map(k => (
            <Card key={k.label} className={`${k.cls} shadow-sm`}>
              <CardContent className="pt-4 pb-3 text-center">
                <p className={`text-2xl font-bold`}>{k.count}</p>
                <p className="text-xs">{k.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Overdue Alert Banner */}
        {overdueActions.length > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="overdue-alert">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">
              {overdueActions.length} action{overdueActions.length > 1 ? 's' : ''} overdue. Oldest: {overdueActions[0]?.assign_to_name} — "{overdueActions[0]?.details?.slice(0, 60)}..."
            </p>
          </div>
        )}

        {/* Tabs + Filters Row */}
        <Tabs defaultValue="all" className="w-full">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <TabsList className="grid grid-cols-5 max-w-2xl">
              <TabsTrigger value="all" data-testid="tab-all">All ({activeActions.length})</TabsTrigger>
              <TabsTrigger value="my" data-testid="tab-my-actions">My Actions ({myActions.filter(a => !['completed','skipped'].includes(a.status)).length})</TabsTrigger>
              <TabsTrigger value="assigned" data-testid="tab-assigned-by-me">I Assigned ({assignedByMe.filter(a => !['completed','skipped'].includes(a.status)).length})</TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completedActions.length})</TabsTrigger>
              {isSuperUser && <TabsTrigger value="admin" data-testid="tab-admin">Admin View</TabsTrigger>}
            </TabsList>

            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36 h-9" data-testid="action-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-36 h-9" data-testid="action-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="1source">1Source</SelectItem>
                </SelectContent>
              </Select>
              <SiteDeptFilter site={filterSite} onSiteChange={setFilterSite} dept={filterDeptAction} onDeptChange={setFilterDeptAction} />
              <DateRangeSelect value={dateRange} onChange={setDateRange} testId="action-date-range" />
            </div>
          </div>

          <TabsContent value="all" className="mt-4">{renderActionList(activeActions)}</TabsContent>
          <TabsContent value="my" className="mt-4">{renderActionList(myActions.filter(a => !['completed','skipped'].includes(a.status)))}</TabsContent>
          <TabsContent value="assigned" className="mt-4">{renderActionList(assignedByMe.filter(a => !['completed','skipped'].includes(a.status)))}</TabsContent>
          <TabsContent value="completed" className="mt-4">{renderActionList(completedActions)}</TabsContent>
          {isSuperUser && <TabsContent value="admin" className="mt-4">{renderActionList(filtered)}</TabsContent>}
        </Tabs>

        {/* Action Detail Dialog */}
        <Dialog open={!!selectedAction} onOpenChange={() => setSelectedAction(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="action-detail-description">
            {selectedAction && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 flex-wrap">
                    Action Detail
                    <Badge className={statusConfig[selectedAction.status]?.color}>{statusConfig[selectedAction.status]?.label}</Badge>
                    <Badge className={priorityConfig[selectedAction.priority]?.color}>{priorityConfig[selectedAction.priority]?.label}</Badge>
                    {selectedAction.is_overdue && <Badge className="bg-red-500 text-white">OVERDUE</Badge>}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2" data-testid="action-detail-dialog">
                  {/* Context */}
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p className="text-sm text-gray-900 font-medium mb-3">{selectedAction.details}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div><span className="font-medium">Assigned to:</span> {selectedAction.assign_to_name}</div>
                      <div><span className="font-medium">Assigned by:</span> {selectedAction.assigned_by_name}</div>
                      <div><span className="font-medium">Department:</span> {selectedAction.department || '--'}</div>
                      <div><span className="font-medium">Created:</span> {new Date(selectedAction.created_at).toLocaleString()}</div>
                      {selectedAction.due_date && <div><span className="font-medium">Due:</span> <span className={selectedAction.is_overdue ? 'text-red-600 font-semibold' : ''}>{new Date(selectedAction.due_date).toLocaleDateString()}</span></div>}
                      {selectedAction.source_module && <div><span className="font-medium">Source:</span> {selectedAction.source_module}</div>}
                      {selectedAction.context?.mpn && <div><span className="font-medium">MPN:</span> <span className="font-mono">{selectedAction.context.mpn}</span></div>}
                      {selectedAction.context?.bomName && <div><span className="font-medium">BOM:</span> {selectedAction.context.bomName}</div>}
                      {selectedAction.context?.savings && <div><span className="font-medium text-green-600">Savings:</span> {selectedAction.context.savings}</div>}
                    </div>
                    {selectedAction.resolution && (
                      <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
                        <p className="text-xs font-semibold text-green-800">Resolution:</p>
                        <p className="text-xs text-green-700">{selectedAction.resolution}</p>
                      </div>
                    )}
                  </div>

                  {/* History Timeline */}
                  {selectedAction.history?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Status History</h4>
                      <div className="space-y-2 ml-3 border-l-2 border-gray-200 pl-4">
                        {selectedAction.history.map((h, i) => (
                          <div key={i} className="relative text-xs">
                            <div className="absolute -left-[21px] top-0.5 w-3 h-3 rounded-full bg-white border-2 border-gray-400" />
                            <span className="font-medium text-gray-700">{h.changed_by_name}</span>
                            <span className="text-gray-400"> &rarr; </span>
                            <Badge className={`${statusConfig[h.status]?.color} text-[9px] px-1 py-0`}>{h.status}</Badge>
                            {h.note && <span className="text-gray-500 ml-1"> — {h.note}</span>}
                            <span className="text-gray-400 ml-2">{new Date(h.timestamp).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Conversation */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Conversation</h4>
                    <div className="space-y-3">
                      {(selectedAction.responses || []).length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No responses yet.</p>
                      ) : (
                        selectedAction.responses.map((r, idx) => (
                          <div key={idx} className="flex gap-3">
                            <Avatar className="h-7 w-7 flex-shrink-0">
                              <AvatarFallback className="bg-blue-100 text-blue-700 text-[10px] font-bold">{r.user?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 bg-gray-50 rounded-lg p-3 border">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">{r.user}</span>
                                <span className="text-xs text-gray-500">{new Date(r.date).toLocaleString()}</span>
                              </div>
                              <p className="text-sm text-gray-700">{r.text}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Reply */}
                  <div className="flex gap-2">
                    <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Add a response..." rows={2} className="flex-1" data-testid="action-reply-input" />
                    <Button onClick={() => handleReply(selectedAction.action_id)} className="bg-blue-600 hover:bg-blue-700 text-white self-end" data-testid="action-reply-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Status Actions */}
                  {!['completed', 'skipped'].includes(selectedAction.status) && (
                    <div className="space-y-2 pt-2 border-t">
                      <Textarea value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Optional note for status change..." rows={1} className="text-xs" />
                      <div className="flex gap-2 flex-wrap">
                        {selectedAction.status === 'pending' && (
                          <Button size="sm" className="bg-blue-600 text-white" onClick={() => handleStatusChange(selectedAction.action_id, 'in_progress')}>
                            <ArrowUpRight className="h-3 w-3 mr-1" /> Mark In Progress
                          </Button>
                        )}
                        <Button size="sm" className="bg-green-600 text-white" onClick={() => handleStatusChange(selectedAction.action_id, 'completed')} data-testid="mark-complete-btn">
                          <CheckCircle className="h-3 w-3 mr-1" /> Complete
                        </Button>
                        <Button size="sm" variant="outline" className="text-orange-700 border-orange-300" onClick={() => handleStatusChange(selectedAction.action_id, 'escalated')}>
                          <ShieldAlert className="h-3 w-3 mr-1" /> Escalate
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={() => handleStatusChange(selectedAction.action_id, 'blocked')}>
                          <Ban className="h-3 w-3 mr-1" /> Blocked
                        </Button>
                        <Button size="sm" variant="outline" className="text-gray-500" onClick={() => handleStatusChange(selectedAction.action_id, 'skipped')} data-testid="mark-skipped-btn">
                          <X className="h-3 w-3 mr-1" /> Skip
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Reassign Modal */}
        <QuickAssignModal open={showReassign} onClose={() => setShowReassign(false)} context={{}} />
      </div>
    </Layout>
  );
};

export default ActionHistory;
